import { ReactNode, useEffect, useState } from "react";

import { ArrowDownIcon, ArrowUpIcon } from "../Icons";
import { Typo } from "../Typo";

export type CollapseProps = {
  isMobile?: boolean;
  expandMode?: "replace" | "stack";
  previewContent:
    | ReactNode
    | ((toggle: () => void, isCollapsed: boolean) => ReactNode);
  detailsContent: ReactNode;
  hideToggleButton?: boolean;
  labels?: {
    expand?: string;
    collapse?: string;
  };
};

const TRANSITION_CLASSES =
  "transition-[max-height,opacity,transform] duration-300 ease-in-out";

const COLLAPSED_CLASSES = "opacity-100 max-h-96 transform scale-100";
const HIDDEN_CLASSES = "opacity-0 max-h-0 transform scale-95 overflow-hidden";
const EXPANDED_CLASSES = "opacity-100 max-h-[2000px] transform scale-100";

export const Collapse = ({
  isMobile = false,
  expandMode = "replace",
  previewContent,
  detailsContent,
  hideToggleButton = false,
  labels = {
    collapse: "Collapse",
    expand: "Expand",
  },
}: CollapseProps) => {
  const [isCollapsed, setIsCollapsed] = useState(true);

  useEffect(() => {
    if (!isMobile) {
      setIsCollapsed(false);
    }
  }, [isMobile]);

  const toggleCollapse = () => setIsCollapsed(!isCollapsed);

  const transitionClasses = isMobile ? TRANSITION_CLASSES : "";

  const isMiniRenderFunction = typeof previewContent === "function";
  const renderedMiniComponent = isMiniRenderFunction
    ? previewContent(toggleCollapse, isCollapsed)
    : previewContent;

  const miniVisibilityClasses = (() => {
    if (!isMobile) {
      return expandMode === "stack" ? EXPANDED_CLASSES : HIDDEN_CLASSES;
    }
    if (expandMode === "stack") return COLLAPSED_CLASSES;
    return isCollapsed ? COLLAPSED_CLASSES : HIDDEN_CLASSES;
  })();

  const fullVisibilityClasses =
    !isMobile || !isCollapsed ? EXPANDED_CLASSES : HIDDEN_CLASSES;

  return (
    <>
      <div className={`${transitionClasses} ${miniVisibilityClasses}`}>
        {renderedMiniComponent}
      </div>

      <div className={`${transitionClasses} ${fullVisibilityClasses}`}>
        {detailsContent}
      </div>

      {isMobile && !hideToggleButton && (
        <button
          id="btn-collapse"
          data-testid="btn-collapse"
          type="button"
          onClick={toggleCollapse}
          className="mt-0.5 flex cursor-pointer flex-row items-center justify-center gap-2"
          aria-expanded={!isCollapsed}
          aria-label={isCollapsed ? labels.expand : labels.collapse}
        >
          <Typo type="label2XS" textColor="text-text-tertiary">
            {isCollapsed ? labels.expand : labels.collapse}
          </Typo>
          {isCollapsed ? (
            <ArrowDownIcon className="h-2 w-2 [&_path]:stroke-text-tertiary" />
          ) : (
            <ArrowUpIcon className="h-2 w-2 [&_path]:stroke-text-tertiary" />
          )}
        </button>
      )}
    </>
  );
};
